<template>
	<Component
		:is="rootComponent"
		ref="root"
		max-width="550"
		closeable
		show-close-button
		@closed="onClosed"
	>
		<div class="age-restriction px-4 pb-3 lg:pt-12">
			<div
				class="age-restriction__img-wrapper w-full mb-4 shrink-0 overflow-hidden rounded-xl"
			>
				<img
					src="https://rahmet.object.pscloud.io/static-files/assets/takeaway/img/age-restriction.png"
					alt="21+"
					class="block h-full w-full object-cover [object-position:center_10%]"
				/>
			</div>

			<div class="mb-3 text-2xl font-bold">{{ t('ageRestriction.title') }}</div>

			<div class="mb-4 text-sm text-secondary">
				<p class="mb-2">
					{{ t('ageRestriction.description.annotation_1') }}
				</p>
				<p class="mb-2">
					{{ t('ageRestriction.description.annotation_2') }}
				</p>
				<p>
					{{ t('ageRestriction.description.annotation_3') }}
				</p>
			</div>

			<div class="flex">
				<UiButton type="light" class="mr-2" @click="cancel">
					{{ t('common.no') }}
				</UiButton>

				<UiButton type="primary" @click="confirm">
					{{ t('ageRestriction.confirmationText') }}
				</UiButton>
			</div>
		</div>
	</Component>
</template>

<script setup lang="ts">
import { ref } from 'vue';
import { useI18n } from 'vue-i18n';
import UiModalComponent from '@/components/ui/UiModal.vue';
import UiBottomSheetComponent from '@/components/ui/UiBottomSheet.vue';
import UiButton from '@/components/ui/UiButton.vue';
import {
	type AgeRestrictionPayload,
	useAgeRestrictionAnalytics
} from '@/composables/analytics/use-age-restriction-analytics';

const { t } = useI18n();
const { trackOnOpened, trackConfirmation, trackCancellation, trackOnClosed } =
	useAgeRestrictionAnalytics();

const rootComponent =
	window.innerWidth >= 768 ? UiModalComponent : UiBottomSheetComponent;

const root = ref<UiModal | UiBottomSheet | null>(null);

let isSelectedByUser = false;
const setIsSelectedByUser = (value: boolean) => {
	isSelectedByUser = value;
};

const analyticsPayload: AgeRestrictionPayload = {
	terminal_id: 0,
	product_id: 0,
	product_name: ''
};

const setAnalyticsPayload = (payload: AgeRestrictionPayload) => {
	analyticsPayload.terminal_id = payload.terminal_id;
	analyticsPayload.product_id = payload.product_id;
	analyticsPayload.product_name = payload.product_name;
};

const clearAnalyticsPayload = () => {
	analyticsPayload.terminal_id = 0;
	analyticsPayload.product_id = 0;
	analyticsPayload.product_name = '';
};

let _resolve: ((value: boolean) => void) | null = null;
const setPromiseHandlers = (resolve: (value: boolean) => void) => {
	_resolve = resolve;
};

const open = () => {
	root.value?.open();

	trackOnOpened(analyticsPayload);
};

const hide = () => {
	root.value?.hide();
};

const confirm = () => {
	setIsSelectedByUser(true);
	trackConfirmation(analyticsPayload);

	_resolve?.(true);
	hide();
};

const cancel = () => {
	setIsSelectedByUser(true);
	trackCancellation(analyticsPayload);

	_resolve?.(false);
	hide();
};

const onClosed = () => {
	if (!isSelectedByUser) {
		trackOnClosed(analyticsPayload);
	}

	setIsSelectedByUser(false);
	clearAnalyticsPayload();
};

defineExpose({
	setAnalyticsPayload,
	setPromiseHandlers,
	open,
	hide
});
</script>

<style scoped lang="scss">
.age-restriction {
	&__img-wrapper {
		height: 168px;

		@media (min-width: 768px) {
			height: 190px;
		}
	}
}
</style>
